import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Clock, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { Widget } from "@/components/dashboard/Widget";

/** "Since your last visit" — deltas the moment the user logs in. */
export const WhatsChangedWidget = ({ size = "md" }) => {
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/whats-changed")
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: false }); })
      .catch(() => { if (alive) setState({ loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, []);

  const isFirst = state.data?.is_first_visit;
  const changes = state.data?.changes || [];

  return (
    <Widget
      title="Since your last visit"
      icon={Clock}
      size={size}
      testId="whats-changed-widget"
    >
      {state.loading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-4 w-full" />
          ))}
        </div>
      )}
      {state.error && (
        <div className="text-sm text-muted-foreground text-center py-4">
          Couldn&apos;t load recent changes.
        </div>
      )}
      {!state.loading && !state.error && state.data && (
        <>
          {isFirst && (
            <div className="text-sm text-muted-foreground py-2" data-testid="whats-changed-first-visit">
              Welcome. As you use CareerOS, this panel will show what changed since your last login — new insights, score moves, jobs matched, financial-plan shifts, and more.
            </div>
          )}
          {!isFirst && changes.length === 0 && (
            <div className="text-sm text-muted-foreground py-2" data-testid="whats-changed-empty">
              No significant changes since your last visit. Keep going — small daily actions compound.
            </div>
          )}
          {changes.length > 0 && (
            <ul className="space-y-2" data-testid="whats-changed-list">
              {changes.map((c, i) => {
                const isNumeric = typeof c.delta === "number";
                const isUp = isNumeric && c.delta > 0;
                return (
                  <motion.li
                    key={c.key}
                    initial={{ opacity: 0, x: -4 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.04 }}
                    className="flex items-center gap-2.5 text-sm"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                        isUp || !isNumeric ? "bg-[hsl(var(--success))]" : "bg-destructive"
                      }`}
                    />
                    <span className="text-foreground/90 flex-1 leading-snug">
                      {c.label}
                      {isNumeric && c.value != null && (
                        <span className="text-muted-foreground text-xs ml-1.5">
                          ({isUp ? "+" : ""}{c.delta} → {c.value})
                        </span>
                      )}
                    </span>
                  </motion.li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </Widget>
  );
};
